import type { CSSProperties } from '@mui/material/styles/createTypography';
import type { Extra, FontFamily, TypographyStyleOverridesInput } from '@config/theme/theme.types';
import type { SxProps, Theme } from '@mui/material/styles';
import type { CSSObject } from '@mui/styled-engine';

export const MS = '"Montserrat", "Roboto", sans-serif';
export const RB = '"Roboto", "Montserrat", sans-serif';
export const GZBK = '"Gazpacho Black", "Gazpacho", sans-serif';
export const GZR = '"Gazpacho", sans-serif';
export const AG = '"Agrandir", sans-serif';
export const IT = '"Inter", sans-serif';
export const RR = '"Road Rage", "Gazpacho", sans-serif';

export const STACKS: Record<FontFamily, string> = {
  montserrat: MS,
  roboto: RB,
  gazpacho: GZBK,
  'gazpacho-regular': GZR,
  agrandir: AG,
  inter: IT,
  'road-rage': RR,
};

export const WEIGHTS: Record<string, number> = {
  light: 300,
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
  extrabold: 800,
  black: 900,
};

export const defaultLh = (px: number) => `${px * 1.5}px`;

export const tok = (ff: string, w: number, s: number, lh?: number, x?: Extra): CSSProperties => ({
  fontFamily: ff,
  fontWeight: w,
  fontSize: `${s}px`,
  lineHeight: lh ? `${lh}px` : defaultLh(s),
  letterSpacing: 0,
  ...x,
});

export const ms = (w: number, s: number, lh?: number, x?: Extra) => tok(MS, w, s, lh, x);
export const rb = (w: number, s: number, lh?: number, x?: Extra) => tok(RB, w, s, lh, x);
export const gzBk = (s: number, lh?: number, x?: Extra) => tok(GZBK, 900, s, lh, x);
export const gzR = (s: number, lh?: number, x?: Extra) => tok(GZR, 900, s, lh, x);
export const ag = (s: number, lh?: number, x?: Extra) => tok(AG, 500, s, lh, x);

export const typographyVariants = {
  // ── MUI semantic variants ─────────────────────────────────────────────
  h1: ag(30, 45),
  h2: ag(24, 36),
  h3: ag(20, 30),
  h4: ag(19, 28.5),
  h5: ms(700, 19, 28.5),
  h6: ms(700, 16, 24),
  subtitle1: ms(600, 16, 24),
  subtitle2: ms(600, 14, 21),
  body1: ms(400, 16, 24),
  body2: ms(400, 14, 21),
  button: ms(600, 16, 20.8, { textTransform: 'none' }),
  caption: ms(500, 12, 18),
  overline: ms(800, 12, 14.628, { textTransform: 'uppercase', letterSpacing: '1.2px' }),

  // ── Gazpacho Black ────────────────────────────────────────────────────
  'gazpacho-900-60': gzBk(60),
  'gazpacho-900-50': gzBk(50, 62.5),
  'gazpacho-900-48': gzBk(48, 60),
  'gazpacho-900-40': gzBk(40, 56),
  'gazpacho-900-35': gzBk(35),
  'gazpacho-900-30': gzBk(30, 37.5),
  'gazpacho-900-25': gzBk(25, 37.5),
  'gazpacho-900-24': gzBk(24, 36),
  'gazpacho-900-22': gzBk(22, 33),
  'gazpacho-900-20': gzBk(20, 30),
  'gazpacho-900-19': gzBk(19, 28.5),

  // ── Gazpacho Regular ──────────────────────────────────────────────────
  'gazpacho-regular-900-50': gzR(50, 75),
  'gazpacho-regular-900-29': gzR(29, 36.25),
  'gazpacho-regular-900-19': gzR(19),

  // ── Agrandir NarrowMedium ─────────────────────────────────────────────
  'agrandir-500-50': ag(50, 75),
  'agrandir-500-30': ag(30, 45),
  'agrandir-500-25': ag(25, 32.5),
  'agrandir-500-24': ag(24, 36),
  'agrandir-500-20': ag(20, 30),
  'agrandir-500-19': ag(19, 28.5),
  'agrandir-500-18': ag(18, 27),
  'agrandir-500-16': ag(16, 24),

  // ── Montserrat 800 ────────────────────────────────────────────────────
  'montserrat-800-40': ms(800, 40, 56),
  'montserrat-800-30': ms(800, 30, 42),
  'montserrat-800-12-uppercase': ms(800, 12, 14.628, { textTransform: 'uppercase', letterSpacing: '1.2px' }),
  // ── Montserrat 700 ────────────────────────────────────────────────────
  'montserrat-700-40': ms(700, 40, 56),
  'montserrat-700-24': ms(700, 24, 36),
  'montserrat-700-20': ms(700, 20, 30),
  'montserrat-700-19': ms(700, 19, 28.5),
  'montserrat-700-16': ms(700, 16, 24),
  'montserrat-700-14': ms(700, 14, 21),
  // ── Montserrat 600 ────────────────────────────────────────────────────
  'montserrat-600-30': ms(600, 30, 45),
  'montserrat-600-24': ms(600, 24, 31.2),
  'montserrat-600-19': ms(600, 19, 28.5),
  'montserrat-600-16': ms(600, 16, 24),
  'montserrat-600-14': ms(600, 14, 21),
  'montserrat-600-13': ms(600, 13, 19.5, { letterSpacing: '0.15px' }),
  'montserrat-600-11-underline': ms(600, 11, 16.5, { textDecoration: 'underline' }),
  // ── Montserrat 500 ────────────────────────────────────────────────────
  'montserrat-500-20': ms(500, 20, 28),
  'montserrat-500-19': ms(500, 19, 28.5),
  'montserrat-500-16': ms(500, 16, 24),
  'montserrat-500-14': ms(500, 14, 21),
  'montserrat-500-12': ms(500, 12, 18),
  // ── Montserrat 400 ────────────────────────────────────────────────────
  'montserrat-400-19': ms(400, 19),
  'montserrat-400-19-strikethrough': ms(400, 19, undefined, { textDecoration: 'line-through' }),
  'montserrat-400-16': ms(400, 16, 24),
  'montserrat-400-16-100': ms(400, 16, 16),
  'montserrat-400-16-strikethrough': ms(400, 16, 24, { textDecoration: 'line-through' }),
  'montserrat-400-15': ms(400, 15, 22.5),
  'montserrat-400-14': ms(400, 14, 21),
  // ── Montserrat 300 ────────────────────────────────────────────────────
  'montserrat-300-25': ms(300, 25, 37.5),
  'montserrat-300-24': ms(300, 24, 36),
  'montserrat-300-19': ms(300, 19, 28.5),

  // ── Roboto 900 ────────────────────────────────────────────────────────
  'roboto-900-19': rb(900, 19),
  // ── Roboto 700 ────────────────────────────────────────────────────────
  'roboto-700-60': rb(700, 60),
  'roboto-700-35': rb(700, 35),
  'roboto-700-25': rb(700, 25, 29.3),
  'roboto-700-24': rb(700, 24, 36),
  'roboto-700-20': rb(700, 20, 30),
  'roboto-700-19': rb(700, 19, 28.5),
  'roboto-700-16': rb(700, 16, 24),
  'roboto-700-16-uppercase': rb(700, 16, 18.75, { textTransform: 'uppercase' }),
  'roboto-700-14': rb(700, 14, 21),
  // ── Roboto 600 ───────────────────────────────── needs variable font ──
  'roboto-600-19': rb(600, 19),
  'roboto-600-18': rb(600, 18, 27),
  'roboto-600-18-uppercase': rb(600, 18, 21.094, { textTransform: 'uppercase' }),
  'roboto-600-16': rb(600, 16, 24),
  'roboto-600-16-uppercase': rb(600, 16, 18.75, { textTransform: 'uppercase' }),
  'roboto-600-14': rb(600, 14, 21),
  'roboto-600-12': rb(600, 12, 14.063),
  'roboto-600-11-underline': rb(600, 11, 16.5, { textDecoration: 'underline' }),
  // ── Roboto 500 ────────────────────────────────────────────────────────
  'roboto-500-30': rb(500, 30, 39),
  'roboto-500-19': rb(500, 19, 28.5),
  'roboto-500-18': rb(500, 18, 25.2),
  'roboto-500-16': rb(500, 16, 24),
  'roboto-500-14': rb(500, 14, 21),
  'roboto-500-14-uppercase': rb(500, 14, 24, { textTransform: 'uppercase', letterSpacing: '0.4px' }),
  'roboto-500-12': rb(500, 12, 18),
  // ── Roboto 400 ────────────────────────────────────────────────────────
  'roboto-400-20': rb(400, 20, 25),
  'roboto-400-20-strikethrough': rb(400, 20, 25, { textDecoration: 'line-through' }),
  'roboto-400-19': rb(400, 19, 28.5),
  'roboto-400-16': rb(400, 16, 24),
  'roboto-400-16-strikethrough': rb(400, 16, 24, { textDecoration: 'line-through' }),
  'roboto-400-15': rb(400, 15, 17.578),
  'roboto-400-14': rb(400, 14, 21),
  // ── Roboto 300 ────────────────────────────────────────────────────────
  'roboto-300-19': rb(300, 19, 23.75),
  'roboto-300-18': rb(300, 18),

  // ── Inter ─────────────────────────────────────────────────────────────
  'inter-700-100': tok(IT, 700, 100, 121),

  // ── Road Rage ─────────────────────────────────────────────────────────
  'road-rage-400-16': tok(RR, 400, 16, 21.424),
};

type TypographyVariantName = keyof typeof typographyVariants;

/**
 * Semantic typography role registry — each role is an additive alias resolving
 * to the exact CSSProperties of an existing typeset variant, so Novus output is
 * byte-identical. Keys are intent-named (the vocabulary call sites adopt in
 * slice-20); values are the typeset variant each role mirrors. Typing the values
 * as `TypographyVariantName` compile-checks every alias against a real variant,
 * guaranteeing deep-equality. Several roles intentionally share one variant where
 * that one treatment serves two distinct intents (e.g. a dialog title that is
 * also a price line) — many roles to one variant is safe; one role to two
 * variants is not (a variant key resolves to a single style).
 */
export const semanticTypographyRoleMap: Record<string, TypographyVariantName> = {
  // headings — page/section structure
  'heading.hero': 'gazpacho-900-50',
  'heading.page': 'gazpacho-900-40',
  'heading.section': 'gazpacho-900-30',
  'heading.subsection': 'agrandir-500-19',
  'heading.detail': 'agrandir-500-16',
  // titles — a named thing (dialog / step / card / plan)
  'title.step': 'gazpacho-900-24',
  'title.dialog': 'gazpacho-900-25',
  'title.dialogCompact': 'gazpacho-900-22',
  'title.dialogCondensed': 'gazpacho-900-19',
  'title.plan': 'agrandir-500-25',
  'title.card': 'agrandir-500-20',
  // prices — money
  'price.hero': 'gazpacho-900-60',
  'price.primary': 'gazpacho-900-35',
  'price.total': 'roboto-700-19',
  'price.display': 'roboto-500-19',
  'price.suffixLight': 'roboto-300-18',
  // labels, tags & errors
  'label.button': 'roboto-700-16',
  'label.buttonEmphatic': 'roboto-700-16-uppercase',
  'discount.text': 'roboto-700-14',
  'label.field': 'roboto-600-16',
  'label.fieldSmall': 'roboto-600-14',
  'error.inline': 'roboto-600-12',
  // body, subtitle & option
  'body.lead': 'roboto-500-18',
  'body.emphasis': 'roboto-500-16',
  'subtitle.card': 'roboto-500-14',
  'body.md': 'roboto-400-16',
  'body.sm': 'roboto-400-14',
  'option.label': 'montserrat-400-16-100',
  // dual-role aliases — a second intent name for a variant that genuinely serves
  // two roles, so slice-20 can pick the alias that reads true at each call site
  'price.suffix': 'gazpacho-900-19', // also title.dialogCondensed
  'price.card': 'gazpacho-900-25', // also title.dialog
  'subtitle.section': 'agrandir-500-20', // also title.card
  'discount.tag': 'roboto-700-16-uppercase', // also label.buttonEmphatic
  'label.fieldMedium': 'roboto-500-14', // also subtitle.card
};

export const semanticTypographyVariants: Record<string, CSSProperties> = Object.fromEntries(
  Object.entries(semanticTypographyRoleMap).map(([role, typeset]) => [role, typographyVariants[typeset]])
);

export const typographyStyleOverrides = ({
  family,
  size,
  weight,
  lineHeight,
  color,
  italic = false,
  uppercase = false,
  capitalize = false,
  underline = false,
  strike = false,
  maxLines,
  ellipsis = false,
}: TypographyStyleOverridesInput): CSSObject => {
  const hasCustom =
    family !== undefined ||
    size !== undefined ||
    weight !== undefined ||
    lineHeight !== undefined ||
    color !== undefined ||
    italic ||
    uppercase ||
    capitalize ||
    underline ||
    strike ||
    maxLines !== undefined ||
    ellipsis;

  if (!hasCustom) return {};

  const resolvedWeight =
    weight !== undefined ? (typeof weight === 'number' ? weight : (WEIGHTS[weight] ?? 400)) : undefined;

  const truncation: CSSObject =
    typeof maxLines === 'number'
      ? {
          display: '-webkit-box',
          WebkitLineClamp: maxLines,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }
      : ellipsis
        ? {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }
        : {};

  return {
    ...(family ? { fontFamily: STACKS[family] } : {}),
    ...(size !== undefined ? { fontSize: `${size}px` } : {}),
    ...(resolvedWeight !== undefined ? { fontWeight: resolvedWeight } : {}),
    ...(lineHeight !== undefined
      ? { lineHeight: `${lineHeight}px` }
      : size !== undefined
        ? { lineHeight: defaultLh(size) }
        : {}),
    ...(color ? { color } : {}),
    ...(italic ? { fontStyle: 'italic' as const } : {}),
    ...(uppercase
      ? { textTransform: 'uppercase' as const }
      : capitalize
        ? { textTransform: 'capitalize' as const }
        : {}),
    ...(underline || strike
      ? { textDecoration: underline && strike ? 'underline line-through' : underline ? 'underline' : 'line-through' }
      : {}),
    ...truncation,
  };
};

// Optional helper if you *want* the return typed as SxProps for sx usage:
export const typographySxOverrides = (input: TypographyStyleOverridesInput): SxProps<Theme> =>
  typographyStyleOverrides(input) as SxProps<Theme>;
